import { MutationStatus } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { useUnsavedChangesStatus } from '@/modules/aiAgents/hooks/useUnsavedChangesStatus';
import { UnsavedChangesStatus } from '@/modules/aiAgents/models';

type UnsavedChangesAlertProps = {
  onSave: () => void;
  saveStatus: MutationStatus;
  hasUnsavedChanges: boolean;
  // Optional: only forms with a character budget (e.g. Guidelines) pass this.
  remainingCharacters?: number;
};

const statusMessageKey: Record<Exclude<UnsavedChangesStatus, null>, string> = {
  error: 'ai_agents.saving_status.unable_to_save_changes',
  saving: 'ai_agents.saving_status.there_are_unsaved_changes',
  unsaved: 'ai_agents.saving_status.there_are_unsaved_changes',
};

export const UnsavedChangesAlert = ({
  onSave,
  saveStatus,
  hasUnsavedChanges,
  remainingCharacters = Number.POSITIVE_INFINITY,
}: UnsavedChangesAlertProps) => {
  const { t } = useTranslation();

  const { status, isSaving, hasError, canSave } = useUnsavedChangesStatus({
    saveStatus,
    hasUnsavedChanges,
    remainingCharacters,
  });

  if (!status) {
    return null;
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!canSave) {
      e.preventDefault();
      return;
    }
    onSave();
  };

  const message = t(statusMessageKey[status]);

  return (
    <div className="flex w-fit items-center gap-3 rounded-xl border border-border bg-background py-2 pr-2 pl-3.5 shadow-sm duration-300 animate-in fade-in slide-in-from-top-2">
      <p className="text-sm leading-6 text-foreground">{message}</p>
      <div className="flex items-center gap-2">
        <InfoTooltip content={t('ai_agents.saving_status.unable_to_save_changes')} open={hasError}>
          <Button
            isLoading={isSaving}
            variant="primary"
            size="sm"
            className="h-7 min-w-14"
            onClick={handleClick}
          >
            {t('common.save')}
          </Button>
        </InfoTooltip>
      </div>
    </div>
  );
};
